import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import {
  SEGMENTS, MIN_INDEXABLE, segmentBySlug, segmentTitle, segmentSearchHref, fetchSegmentStats, fuelLabel,
} from "@/lib/segmentStats";

const eur = (n: number | null) => (n == null ? "–" : `${n.toLocaleString("ro-RO")} €`);
const num = (n: number | null) => (n == null ? "–" : n.toLocaleString("ro-RO"));
const MONTHS = ["ian", "feb", "mar", "apr", "mai", "iun", "iul", "aug", "sep", "oct", "nov", "dec"];
const monthLabel = (k: string) => `${MONTHS[Number(k.slice(5, 7)) - 1]} ${k.slice(0, 4)}`;
const dateRo = (iso: string) => new Date(iso).toLocaleDateString("ro-RO", { day: "2-digit", month: "2-digit", year: "numeric" });

export const Route = createFileRoute("/statistici/$segment/")({
  loader: async ({ params }) => {
    const seg = segmentBySlug(params.segment);
    if (!seg) throw notFound();
    const stats = await fetchSegmentStats(seg);
    return { seg, stats };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Statistici | RO Auto Market" }] };
    const { seg, stats } = loaderData;
    const url = `https://roautomarket.ro/statistici/${seg.slug}`;
    const title = `${segmentTitle(seg)} | RO Auto Market`;
    const description = `${stats.total} anunțuri ${seg.name} active acum pe RO Auto Market. Preț median ${eur(stats.medianPrice)}, prețuri pe modele și orașe, actualizate zilnic.`.slice(0, 160);
    const indexable = stats.total >= MIN_INDEXABLE;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: indexable ? "index, follow" : "noindex, follow" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Dataset",
            name: segmentTitle(seg),
            description,
            url,
            dateModified: stats.generatedAt,
            creator: { "@type": "Organization", name: "RO Auto Market", url: "https://roautomarket.ro" },
            spatialCoverage: "România",
            variableMeasured: ["preț cerut (EUR)", "an fabricație", "kilometraj"],
          }),
        },
      ],
    };
  },
  component: SegmentStatsPage,
});

function SegmentStatsPage() {
  const { seg, stats } = Route.useLoaderData();
  const others = SEGMENTS.filter((s) => s.slug !== seg.slug);
  const months = stats.months.filter((m) => m.count > 0);

  return (
    <main className="container mx-auto max-w-4xl px-4 py-12 space-y-10">
      <div className="surface-dark band-bleed -mt-12 pt-4 pb-6">
        <Breadcrumbs items={[{ label: "Statistici", href: "/statistici-auto-romania" }, { label: seg.name, href: "#" }]} />
        <h1 className="text-3xl font-semibold">{segmentTitle(seg)}</h1>
        <p className="mt-2 opacity-80">Date din anunțurile active pe RO Auto Market. Actualizat {dateRo(stats.generatedAt)}.</p>
      </div>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Anunțuri active" value={num(stats.total)} />
        <Kpi label="Preț median" value={eur(stats.medianPrice)} />
        <Kpi label="An median" value={stats.medianYear == null ? "–" : String(stats.medianYear)} />
        <Kpi label="Kilometraj median" value={stats.medianKm == null ? "–" : `${num(stats.medianKm)} km`} />
      </section>

      <section className="space-y-2">
        <h2 className="text-xl font-semibold">Cât costă</h2>
        <p>
          Jumătate din anunțurile cu preț ({stats.priced}) cer între <strong>{eur(stats.p25)}</strong> și{" "}
          <strong>{eur(stats.p75)}</strong>. Cel mai ieftin: {eur(stats.minPrice)}. Cel mai scump: {eur(stats.maxPrice)}.
        </p>
      </section>

      {stats.models.length > 0 && (
        <Table
          title={seg.kind === "make" ? "Modele" : "Mărci și modele"}
          head={["Model", "Anunțuri", "Preț median"]}
          rows={stats.models.map((m) => [m.label, num(m.count), eur(m.medianPrice)])}
        />
      )}

      {stats.fuels.length > 0 && (
        <Table
          title="Combustibil"
          head={["Combustibil", "Anunțuri", "Preț median"]}
          rows={stats.fuels.map((m) => [fuelLabel(m.label), num(m.count), eur(m.medianPrice)])}
        />
      )}

      {stats.cities.length > 0 && (
        <Table
          title="Orașe"
          head={["Oraș", "Anunțuri", "Preț median"]}
          rows={stats.cities.map((m) => [m.label, num(m.count), eur(m.medianPrice)])}
        />
      )}

      {months.length > 0 && (
        <Table
          title="Anunțuri noi pe lună"
          note="Prețul median e afișat doar pentru lunile cu cel puțin 5 anunțuri noi."
          head={["Luna", "Anunțuri noi", "Preț median cerut"]}
          rows={months.map((m) => [monthLabel(m.month), num(m.count), eur(m.medianPrice)])}
        />
      )}

      <section className="space-y-2">
        <h2 className="text-xl font-semibold">Cum calculăm</h2>
        <ul className="list-disc pl-6 space-y-1 text-sm">
          <li>Folosim doar anunțurile active pe RO Auto Market în România, la momentul încărcării paginii.</li>
          <li>Prețurile sunt cele cerute de vânzători, în euro. Nu sunt prețuri de vânzare finale.</li>
          <li>Mediana e valoarea din mijloc. Nu e influențată de câteva anunțuri foarte scumpe sau foarte ieftine.</li>
          <li>Kilometrajele imposibile sunt ignorate ({stats.kmCount} anunțuri au kilometraj valid).</li>
          <li>Nu acoperim toată piața din România, doar anunțurile de pe site.</li>
        </ul>
      </section>

      <div>
        <Link to={segmentSearchHref(seg) as any} className="inline-flex rounded-md bg-primary px-5 py-3 font-medium text-primary-foreground">
          Vezi anunțurile {seg.name}
        </Link>
      </div>

      <nav className="space-y-2">
        <h2 className="text-xl font-semibold">Alte statistici</h2>
        <div className="flex flex-wrap gap-2">
          {others.map((s) => (
            <Link key={s.slug} to="/statistici/$segment" params={{ segment: s.slug }} className="rounded-full border border-border px-3 py-1 text-sm">
              {s.name}
            </Link>
          ))}
        </div>
      </nav>
    </main>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </div>
  );
}

function Table({ title, head, rows, note }: { title: string; head: string[]; rows: string[][]; note?: string }) {
  return (
    <section className="space-y-2">
      <h2 className="text-xl font-semibold">{title}</h2>
      {note && <p className="text-sm text-muted-foreground">{note}</p>}
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>{head.map((h, i) => <th key={h} className={`px-3 py-2 font-medium ${i ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r[0]} className="border-t border-border">
                {r.map((c, i) => <td key={i} className={`px-3 py-2 ${i ? "text-right tabular-nums" : ""}`}>{c}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
